import { Copy, CreditCard, DollarSign, Edit, Mail, MessageSquare, Plus, RefreshCw, Smartphone, Trash2, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ManagementServicePlanPolicy from '../../components/BillingManagement/ManagementServicePlanPolicy';
import EmailSettingsForm from '../../components/EmailSettingsForm';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const SHARED_TEMPLATE_VARIABLES = {
  property_owner_name: '임대사업자명',
  company_name: '회사명',
  tenant_name: '임차인명',
  building_name: '건물명',
  unit_number: '호실/호수',
  contact_phone: '문의전화',
  contact_email: '문의이메일'
};

const CATEGORY_TEMPLATE_VARIABLES = {
  billing: {
    billing_month: '청구월',
    total_amount: '총금액',
    monthly_rent: '월세',
    maintenance_fee: '관리비',
    other_fee: '기타청구액',
    due_date: '납부기한',
    statement_id: '청구서번호',
    account_bank: '계좌은행',
    account_number: '계좌번호',
    account_holder: '예금주'
  },
  overdue_management: {
    overdue_amount: '연체금액',
    overdue_days: '연체일수',
    overdue_months: '미납개월수',
    late_fee_amount: '연체료',
    total_amount: '총연체금액',
    due_date: '납기일',
    overdue_stage: '연체단계',
    billing_month: '청구월',
    fee_type: '청구항목',
    amount: '금액',
    company_phone: '대표문의전화'
  },
  contract_renewal: {
    expiry_date: '계약만료일',
    response_due_date: '회신기한'
  },
  maintenance_notice: {
    scheduled_date: '예정일',
    scheduled_time: '예정시간',
    work_summary: '작업내용',
    notice: '참고사항'
  },
  tenant_notice: {
    move_in_date: '입주일',
    notice: '공지내용',
    notice_channel: '안내채널'
  },
  vacancy_management: {
    available_date: '입주가능일',
    broker_name: '중개사명',
    broker_phone: '중개사연락처'
  }
};

const getSuggestedVariables = (category) => ({
  ...SHARED_TEMPLATE_VARIABLES,
  ...(CATEGORY_TEMPLATE_VARIABLES[category] || {})
});

const parseTemplateVariables = (variables) => {
  if (!variables) return {};

  if (typeof variables === 'object') {
    return variables;
  }

  try {
    return JSON.parse(variables);
  } catch (error) {
    return {};
  }
};

const stringifyTemplateVariables = (variables) => (
  JSON.stringify(variables, null, 2)
);

const COMMON_MODULE_TABS = [
  { id: 'templates', label: '메시지 템플릿', icon: MessageSquare },
  { id: 'email', label: '이메일 설정', icon: Mail },
  { id: 'management-service-plans', label: '임대관리 요금정책', icon: DollarSign },
  { id: 'sms-usage', label: 'SMS 사용관리', icon: Smartphone },
  { id: 'realtyhub-partners', label: '파트너 중개사', icon: Users }
];

const SMS_USAGE_ENABLED = false;
const RMC_ROLES = ['admin', 'rental_management_company', 'rmc_admin', 'rmc_staff'];

const CommonModuleManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('templates');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTemplateEditModal, setShowTemplateEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState({
    template_name: '',
    template_type: 'sms',
    category: 'general',
    subject: '',
    content: '',
    variables: '{}'
  });
  const [templatePreview, setTemplatePreview] = useState('');
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [partnerForm, setPartnerForm] = useState({
    company_name: '',
    agent_name: '',
    phone: '',
    email: '',
    region: '',
    specialty: ''
  });
  const [smsTopupCredits, setSmsTopupCredits] = useState(1);

  // 이메일 설정 상태
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailSettingsId, setEmailSettingsId] = useState(null);
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  const queryClient = useQueryClient();
  const isRmcUser = RMC_ROLES.includes(user?.role) || (user?.user_type === 'rmc_user' && user?.role === 'admin');
  const isPropertyOwner = user?.role === 'property_owner';
  const visibleTabs = COMMON_MODULE_TABS.filter((tab) => {
    if (tab.id === 'sms-usage') {
      return SMS_USAGE_ENABLED && isRmcUser;
    }
    if (tab.id === 'management-service-plans') {
      return isRmcUser;
    }
    if (tab.id === 'realtyhub-partners') {
      return isRmcUser || isPropertyOwner;
    }
    return isRmcUser || isPropertyOwner;
  });

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [activeTab, visibleTabs]);

  // 메시지 템플릿 조회
  const { data: messageTemplates, isLoading: templatesLoading, refetch: refetchTemplates } = useQuery({
    queryKey: ['message-templates', user?.id, user?.role],
    queryFn: async () => {
      console.log('🔍 메시지 템플릿 API 호출 시작');
      try {
        const response = await api.get('/message-templates');
        console.log('✅ 메시지 템플릿 API 응답:', response);
        return response;
      } catch (error) {
        console.error('❌ 메시지 템플릿 API 오류:', error);
        throw error;
      }
    },
    enabled: !!user && (isRmcUser || isPropertyOwner)
  });

  // 이메일 설정 조회
  const { data: emailSettingsData, isLoading: emailLoading, refetch: refetchEmailSettings } = useQuery({
    queryKey: ['email-settings', user?.id, user?.role, user?.property_owner_id || null, user?.rental_management_company_id || null],
    queryFn: () => api.get('/user-settings/email-settings'),
    enabled: !!user && (isRmcUser || isPropertyOwner)
  });

  const { data: partnersData, isLoading: partnersLoading, refetch: refetchPartners } = useQuery({
    queryKey: ['realtyhub-partners', user?.id, user?.role, partnerSearchTerm],
    queryFn: () => {
      const params = new URLSearchParams({ status: 'active' });
      if (partnerSearchTerm) params.set('search', partnerSearchTerm);
      return api.get(`/partners?${params.toString()}`);
    },
    enabled: !!user && (isRmcUser || isPropertyOwner) && activeTab === 'realtyhub-partners'
  });

  const { data: smsQuotaData, isLoading: smsQuotaLoading, refetch: refetchSmsQuota } = useQuery({
    queryKey: ['sms-quota-summary'],
    queryFn: () => api.get('/sms-quota/summary'),
    enabled: !!user && isRmcUser && SMS_USAGE_ENABLED && activeTab === 'sms-usage'
  });

  const syncPartnerMutation = useMutation(
    () => api.post('/partners/sync', { partners: [] }),
    {
      onSuccess: (response) => {
        alert(`동기화 확인 완료: 전체 ${response.total || 0}건`);
        refetchPartners();
      },
      onError: (error) => {
        alert(error.response?.data?.error || '파트너 동기화에 실패했습니다.');
      }
    }
  );

  const savePartnerMutation = useMutation(
    () => {
      const payload = {
        ...partnerForm,
        status: 'active'
      };
      if (editingPartnerId) {
        return api.put(`/partners/${editingPartnerId}`, payload);
      }
      return api.post('/partners', payload);
    },
    {
      onSuccess: () => {
        alert(editingPartnerId ? '파트너가 수정되었습니다.' : '파트너가 등록되었습니다.');
        setEditingPartnerId(null);
        setPartnerForm({
          company_name: '',
          agent_name: '',
          phone: '',
          email: '',
          region: '',
          specialty: ''
        });
        queryClient.invalidateQueries('realtyhub-partners');
        refetchPartners();
      },
      onError: (error) => {
        alert(error.response?.data?.error || '파트너 저장에 실패했습니다.');
      }
    }
  );

  const deletePartnerMutation = useMutation(
    (partnerId) => api.delete(`/partners/${partnerId}`),
    {
      onSuccess: () => {
        alert('파트너가 비활성화되었습니다.');
        queryClient.invalidateQueries('realtyhub-partners');
        refetchPartners();
      },
      onError: (error) => {
        alert(error.response?.data?.error || '파트너 비활성화에 실패했습니다.');
      }
    }
  );

  const smsTopupMutation = useMutation(
    () => api.post('/sms-quota/topup', {
      credits: Number(smsTopupCredits || 0)
    }),
    {
      onSuccess: () => {
        alert('SMS 충전이 완료되었습니다.');
        refetchSmsQuota();
      },
      onError: (error) => {
        alert(error.response?.data?.error || 'SMS 충전에 실패했습니다.');
      }
    }
  );

  // 이메일 설정 데이터 처리
  useEffect(() => {
    if (emailSettingsData?.data) {
      setEmailSettings(emailSettingsData.data);
      setEmailSettingsId(emailSettingsData.data.id);
    } else if (emailSettingsData && emailSettingsData.data === null) {
      setEmailSettings(null);
      setEmailSettingsId(null);
    }
  }, [emailSettingsData]);

  useEffect(() => {
    setEmailSettings(null);
    setEmailSettingsId(null);
  }, [user?.id, user?.role, user?.property_owner_id, user?.rental_management_company_id]);

  // 템플릿 미리보기 업데이트
  useEffect(() => {
    if (editingTemplate.content) {
      setTemplatePreview(editingTemplate.content);
    }
  }, [editingTemplate.content]);

  const mergeTemplateVariables = (category, currentVariables) => (
    stringifyTemplateVariables({
      ...getSuggestedVariables(category),
      ...parseTemplateVariables(currentVariables)
    })
  );

  // 템플릿 저장
  const handleSaveTemplate = async () => {
    try {
      const templateData = {
        template_name: editingTemplate.template_name,
        template_type: editingTemplate.template_type,
        category: editingTemplate.category,
        subject: editingTemplate.subject,
        content: editingTemplate.content,
        variables: editingTemplate.variables
      };

      let response;
      if (editingTemplate.id) {
        // 수정
        response = await api.put(`/message-templates/${editingTemplate.id}`, templateData);
      } else {
        // 추가
        response = await api.post('/message-templates', templateData);
      }

      if (response.success) {
        alert(editingTemplate.id ? '템플릿이 수정되었습니다.' : '템플릿이 추가되었습니다.');
        setShowTemplateEditModal(false);
        refetchTemplates();
      }
    } catch (error) {
      console.error('템플릿 저장 실패:', error);
      alert('템플릿 저장에 실패했습니다.');
    }
  };

  // 템플릿 복사
  const handleCopyTemplate = async (template) => {
    try {
      const templateData = {
        template_name: `${template.template_name} (복사본)`,
        template_type: template.template_type,
        category: template.category,
        subject: template.subject,
        content: template.content,
        variables: template.variables
      };

      const response = await api.post('/message-templates', templateData);
      if (response.success) {
        alert('템플릿이 복사되었습니다.');
        refetchTemplates();
      }
    } catch (error) {
      console.error('템플릿 복사 실패:', error);
      alert('템플릿 복사에 실패했습니다.');
    }
  };

  // 템플릿 삭제
  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm('정말로 이 템플릿을 삭제하시겠습니까?')) {
      try {
        const response = await api.delete(`/message-templates/${templateId}`);
        if (response.success) {
          alert('템플릿이 삭제되었습니다.');
          refetchTemplates();
        }
      } catch (error) {
        console.error('템플릿 삭제 실패:', error);
        alert('템플릿 삭제에 실패했습니다.');
      }
    }
  };

  // 템플릿 편집 모달 열기
  const handleEditTemplate = (template) => {
    setEditingTemplate({
      id: template.id,
      template_name: template.template_name,
      template_type: template.template_type,
      category: template.category,
      subject: template.subject || '',
      content: template.content,
      variables: mergeTemplateVariables(template.category, template.variables)
    });
    setShowTemplateEditModal(true);
  };

  // 새 템플릿 추가
  const handleAddTemplate = () => {
    setEditingTemplate({
      template_name: '',
      template_type: 'sms',
      category: 'general',
      subject: '',
      content: '',
      variables: mergeTemplateVariables('general', '{}')
    });
    setShowTemplateEditModal(true);
  };

  const availableVariables = Object.entries(
    getSuggestedVariables(editingTemplate.category)
  );

  const startEditPartner = (partner) => {
    setEditingPartnerId(partner.id);
    setPartnerForm({
      company_name: partner.company_name || '',
      agent_name: partner.agent_name || '',
      phone: partner.phone || '',
      email: partner.email || '',
      region: partner.region || '',
      specialty: partner.specialty || ''
    });
  };

  const cancelPartnerEdit = () => {
    setEditingPartnerId(null);
    setPartnerForm({
      company_name: '',
      agent_name: '',
      phone: '',
      email: '',
      region: '',
      specialty: ''
    });
  };

  const submitPartnerForm = (event) => {
    event.preventDefault();
    if (!partnerForm.company_name.trim()) {
      alert('중개사무소명을 입력해주세요.');
      return;
    }
    savePartnerMutation.mutate();
  };

  const insertTemplateVariable = (placeholder) => {
    setEditingTemplate((prev) => ({
      ...prev,
      content: `${prev.content}${prev.content ? ' ' : ''}${placeholder}`
    }));
  };

  // 이메일 설정 저장
  const handleEmailSettingsSubmit = async (formData) => {
    try {
      let resp;
      if (emailSettingsId) {
        resp = await api.put(`/user-settings/email-settings/${emailSettingsId}`, formData);
      } else {
        resp = await api.post('/user-settings/email-settings', formData);
      }

      if (resp && resp.success) {
        alert('이메일 설정이 저장되었습니다.');
        setEmailSettings(resp.data);
        setEmailSettingsId(resp.data?.id || emailSettingsId);
        refetchEmailSettings();
      } else if (resp && resp.data) {
        alert('이메일 설정이 저장되었습니다.');
        setEmailSettings(resp.data);
        setEmailSettingsId(resp.data?.id || emailSettingsId);
        refetchEmailSettings();
      }
    } catch (error) {
      console.error('이메일 설정 저장 실패:', error);
      alert('이메일 설정 저장에 실패했습니다.');
    }
  };

  // 이메일 설정 테스트 발송
  const handleEmailSettingsTest = async () => {
    try {
      if (!emailSettingsId) {
        alert('먼저 이메일 설정을 저장해주세요.');
        return;
      }
      const testEmail = window.prompt('테스트 이메일 주소를 입력하세요');
      if (!testEmail) return;
      const resp = await api.post(`/user-settings/email-settings/${emailSettingsId}/test`, {
        test_email: testEmail
      });
      if (resp && resp.success) {
        alert(resp.message || '테스트 이메일 요청이 전송되었습니다.');
      } else if (resp && resp.data) {
        alert(resp.data?.message || '테스트 이메일 요청이 전송되었습니다.');
      }
    } catch (error) {
      console.error('이메일 설정 테스트 실패:', error);
      alert('이메일 설정 테스트에 실패했습니다.');
    }
  };

  // 필터링된 템플릿 목록


  console.log('🔍 messageTemplates 데이터:', messageTemplates);
  const templatesData = messageTemplates?.data || messageTemplates || [];
  const partners = partnersData?.data || [];
  const smsQuotaPayload = smsQuotaData?.data || smsQuotaData || {};
  const smsAccount = smsQuotaPayload.account || {};
  const smsTransactions = smsQuotaPayload.transactions || [];
  const smsTopupUnits = Number(smsTopupCredits || 0) * 10;
  console.log('🔍 templatesData:', templatesData);
  
  const filteredTemplates = Array.isArray(templatesData) 
    ? templatesData.filter(template =>
        template.template_name.toLowerCase().includes(templateSearchTerm.toLowerCase()) ||
        template.category.toLowerCase().includes(templateSearchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* 헤더 */}
        <div className="mb-5 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">공통모듈관리</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            메시지 템플릿과 이메일 설정을 통합 관리합니다.
          </p>
        </div>

        {/* 탭 네비게이션 */}
        <div className="mb-5 sm:mb-6">
          <nav className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-2" aria-label="공통모듈 관리 탭">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex min-h-[48px] items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors sm:min-h-0 sm:w-auto sm:rounded-md ${
                    isActive
                      ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="min-w-0 leading-snug break-keep">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* 메시지 템플릿 탭 */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* 템플릿 목록 헤더 */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-end gap-3">
                <h2 className="text-xl font-semibold text-gray-900 break-keep">메시지 템플릿</h2>
                <span className="text-sm text-gray-500">
                  총 {filteredTemplates.length}개
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:flex sm:items-center sm:gap-3">
                <input
                  type="text"
                  placeholder="템플릿 검색..."
                  value={templateSearchTerm}
                  onChange={(e) => setTemplateSearchTerm(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-56"
                />
                <button
                  onClick={handleAddTemplate}
                  className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  새 템플릿
                </button>
              </div>
            </div>

            {/* 템플릿 목록 */}
            <div className="bg-white shadow rounded-lg">
              {templatesLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">템플릿을 불러오는 중...</p>
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">템플릿이 없습니다</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    새로운 메시지 템플릿을 추가해보세요.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 sm:gap-5 sm:p-5 lg:grid-cols-3">
                  {filteredTemplates.map((template) => (
                    <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900 break-keep">{template.template_name}</h3>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              template.template_type === 'sms' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {template.template_type === 'sms' ? 'SMS' : '이메일'}
                            </span>
                            <span className="text-xs text-gray-500">{template.category}</span>
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 space-x-1">
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="편집"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleCopyTemplate(template)}
                            className="p-1 text-gray-400 hover:text-green-600"
                            title="복사"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        {template.content.length > 100 
                          ? `${template.content.substring(0, 100)}...` 
                          : template.content}
                      </div>
                      {template.subject && (
                        <div className="text-xs text-gray-500 mt-2">
                          제목: {template.subject}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 이메일 설정 탭 */}
        {activeTab === 'email' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">이메일 설정</h2>
            </div>

            <div className="bg-white shadow rounded-lg">
              <div className="p-6">
                <EmailSettingsForm
                  onSubmit={handleEmailSettingsSubmit}
                  onTest={handleEmailSettingsTest}
                  onCancel={() => setActiveTab('templates')}
                  initialData={emailSettings}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'management-service-plans' && isRmcUser && (
          <ManagementServicePlanPolicy />
        )}

        {activeTab === 'sms-usage' && SMS_USAGE_ENABLED && isRmcUser && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">SMS 사용관리</h2>
                <p className="mt-1 text-sm text-gray-600">
                  SaaS 기본 무료 500건과 SoftFlo 크레딧 충전분을 관리합니다.
                </p>
              </div>
              <button
                onClick={() => refetchSmsQuota()}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                새로고침
              </button>
            </div>

            {smsQuotaLoading ? (
              <div className="bg-white shadow rounded-lg p-8 text-center text-sm text-gray-500">
                SMS 사용현황을 불러오는 중입니다.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {[
                    ['기본 제공', smsAccount.monthly_free_quota || 500, '매월 구독료 포함'],
                    ['무료 잔여', smsAccount.free_remaining_count || 0, `사용 ${smsAccount.free_used_count || 0}건`],
                    ['충전 잔여', smsAccount.paid_balance_count || 0, 'SoftFlo 크레딧 충전분'],
                    ['총 발송 가능', smsAccount.total_available_count || 0, '무료+충전 잔여'],
                    ['이번 달 발송', smsAccount.total_sent_count || 0, smsAccount.billing_month || '-']
                  ].map(([label, value, caption]) => (
                    <div key={label} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="text-xs font-medium text-gray-500">{label}</div>
                      <div className="mt-2 text-2xl font-bold text-gray-900">{Number(value).toLocaleString()}건</div>
                      <div className="mt-1 text-xs text-gray-500">{caption}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="bg-white shadow rounded-lg p-5 lg:col-span-1">
                    <div className="flex items-center gap-2 mb-4">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <h3 className="text-base font-semibold text-gray-900">SMS 충전</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">사용할 SoftFlo 크레딧</label>
                        <input
                          type="number"
                          min="1"
                          value={smsTopupCredits}
                          onChange={(e) => setSmsTopupCredits(e.target.value)}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="rounded-md bg-blue-50 border border-blue-100 p-3 text-sm text-blue-800">
                        {Number(smsTopupCredits || 0).toLocaleString()} 크레딧 사용 = SMS {smsTopupUnits.toLocaleString()}건 충전
                      </div>
                      <button
                        type="button"
                        disabled={smsTopupMutation.isLoading || Number(smsTopupCredits || 0) <= 0}
                        onClick={() => smsTopupMutation.mutate()}
                        className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        SoftFlo 크레딧으로 충전
                      </button>
                    </div>
                  </div>

                  <div className="bg-white shadow rounded-lg lg:col-span-2">
                    <div className="p-5 border-b border-gray-200">
                      <h3 className="text-base font-semibold text-gray-900">최근 SMS 원장</h3>
                    </div>
                    {smsTransactions.length === 0 ? (
                      <div className="p-8 text-center text-sm text-gray-500">아직 SMS 거래 내역이 없습니다.</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {smsTransactions.map((tx) => (
                          <div key={tx.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {tx.transaction_type === 'topup' ? '충전' : tx.transaction_type === 'send' ? '발송 차감' : tx.transaction_type}
                                <span className={`ml-2 ${Number(tx.sms_count || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                  {Number(tx.sms_count || 0).toLocaleString()}건
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-gray-500">{tx.description || tx.source_module || '-'}</div>
                            </div>
                            <div className="text-xs text-gray-500 sm:text-right">
                              <div>{tx.created_at ? new Date(tx.created_at).toLocaleString() : '-'}</div>
                              <div>충전잔여 {Number(tx.balance_after || 0).toLocaleString()}건</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}


        {activeTab === 'realtyhub-partners' && (isRmcUser || isPropertyOwner) && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {isPropertyOwner ? '파트너 중개사' : 'RealtyHub 파트너 동기화'}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {isPropertyOwner
                    ? '중개요청에 사용할 내 전용 파트너 중개사를 관리합니다.'
                    : '공실관리 발송 대상 RMC 공용 파트너를 관리합니다.'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => refetchPartners()}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  새로고침
                </button>
                {isRmcUser && (
                  <button
                    onClick={() => syncPartnerMutation.mutate()}
                    disabled={syncPartnerMutation.isLoading}
                    className="inline-flex items-center px-3 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    동기화 확인
                  </button>
                )}
              </div>
            </div>

            <form onSubmit={submitPartnerForm} className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <Plus className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-semibold text-gray-900">
                  {editingPartnerId ? '파트너 수정' : '파트너 등록'}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={partnerForm.company_name}
                  onChange={(e) => setPartnerForm({ ...partnerForm, company_name: e.target.value })}
                  placeholder="중개사무소명 *"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={partnerForm.agent_name}
                  onChange={(e) => setPartnerForm({ ...partnerForm, agent_name: e.target.value })}
                  placeholder="담당 중개사"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="tel"
                  value={partnerForm.phone}
                  onChange={(e) => setPartnerForm({ ...partnerForm, phone: e.target.value })}
                  placeholder="전화번호"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="email"
                  value={partnerForm.email}
                  onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                  placeholder="이메일"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={partnerForm.region}
                  onChange={(e) => setPartnerForm({ ...partnerForm, region: e.target.value })}
                  placeholder="지역"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={partnerForm.specialty}
                  onChange={(e) => setPartnerForm({ ...partnerForm, specialty: e.target.value })}
                  placeholder="전문분야"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mt-4 flex justify-end gap-2">
                {editingPartnerId && (
                  <button
                    type="button"
                    onClick={cancelPartnerEdit}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    취소
                  </button>
                )}
                <button
                  type="submit"
                  disabled={savePartnerMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingPartnerId ? '수정' : '등록'}
                </button>
              </div>
            </form>

            <div>
              <div className="bg-white shadow rounded-lg">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-3">
                  <div className="text-sm text-gray-600">총 {partners.length}건</div>
                  <input
                    type="text"
                    value={partnerSearchTerm}
                    onChange={(e) => setPartnerSearchTerm(e.target.value)}
                    placeholder="회사명, 담당자, 지역 검색"
                    className="w-64 max-w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {partnersLoading ? (
                  <div className="p-8 text-center text-sm text-gray-500">파트너 목록을 불러오는 중...</div>
                ) : partners.length === 0 ? (
                  <div className="p-8 text-center">
                    <Users className="mx-auto h-10 w-10 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">등록된 파트너가 없습니다.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">회사/담당자</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">연락처</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">지역/구분</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">출처</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">작업</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {partners.map((partner) => (
                          <tr key={partner.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{partner.company_name}</div>
                              <div className="text-xs text-gray-500">{partner.agent_name || '-'}</div>
                              {partner.realtyhub_partner_id && (
                                <div className="text-xs text-gray-400">RealtyHub ID: {partner.realtyhub_partner_id}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div>{partner.phone || '-'}</div>
                              <div className="text-xs text-gray-500">{partner.email || '-'}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div>{partner.region || '-'}</div>
                              <div className="text-xs text-gray-500">{partner.specialty || '-'}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {partner.source === 'manual' ? '직접등록' : 'RealtyHub'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => startEditPartner(partner)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 hover:text-blue-900"
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                수정
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm('파트너를 비활성화하시겠습니까?')) {
                                    deletePartnerMutation.mutate(partner.id);
                                  }
                                }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 hover:text-red-900"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                비활성화
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 템플릿 편집 모달 */}
        {showTemplateEditModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingTemplate.id ? '템플릿 편집' : '새 템플릿 추가'}
                  </h3>
                  <button
                    onClick={() => setShowTemplateEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="sr-only">닫기</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 편집 폼 */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">템플릿 이름</label>
                      <input
                        type="text"
                        value={editingTemplate.template_name}
                        onChange={(e) => setEditingTemplate({...editingTemplate, template_name: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">타입</label>
                        <select
                          value={editingTemplate.template_type}
                          onChange={(e) => setEditingTemplate({...editingTemplate, template_type: e.target.value})}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="sms">SMS</option>
                          <option value="email">이메일</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">카테고리</label>
                        <select
                          value={editingTemplate.category}
                          onChange={(e) => setEditingTemplate({
                            ...editingTemplate,
                            category: e.target.value,
                            variables: mergeTemplateVariables(
                              e.target.value,
                              editingTemplate.variables
                            )
                          })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="general">일반</option>
                          <option value="contract_renewal">계약 갱신</option>
                          <option value="brokerage_request">중개 요청</option>
                          <option value="payment_request">납부 요청</option>
                          <option value="billing">청구서 발송</option>
                          <option value="overdue_management">연체관리</option>
                          <option value="vacancy_management">공실관리</option>
                        </select>
                      </div>
                    </div>

                    {editingTemplate.template_type === 'email' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">이메일 제목</label>
                        <input
                          type="text"
                          value={editingTemplate.subject}
                          onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700">내용</label>
                      <textarea
                        rows={8}
                        value={editingTemplate.content}
                        onChange={(e) => setEditingTemplate({...editingTemplate, content: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="메시지 내용을 입력하세요..."
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">변수(JSON)</label>
                        <span className="text-xs text-gray-500">템플릿 설명용 메타데이터</span>
                      </div>
                      <textarea
                        rows={8}
                        value={editingTemplate.variables}
                        onChange={(e) => setEditingTemplate({...editingTemplate, variables: e.target.value})}
                        className="mt-1 block w-full font-mono text-xs border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder='{"property_owner_name":"임대사업자명"}'
                      />
                    </div>
                  </div>

                  {/* 미리보기 */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">미리보기</h4>
                    <div className="border border-gray-200 rounded-md p-4 bg-gray-50 min-h-[200px]">
                      <div className="text-sm text-gray-600 whitespace-pre-wrap">
                        {templatePreview || '내용을 입력하면 미리보기가 표시됩니다.'}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700">사용 가능 변수</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        클릭하면 본문 끝에 바로 추가됩니다.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {availableVariables.map(([key, label]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => insertTemplateVariable(`{${key}}`)}
                            className="px-2 py-1 rounded-full border border-blue-200 bg-blue-50 text-xs text-blue-700 hover:bg-blue-100"
                            title={label}
                          >
                            {`{${key}}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowTemplateEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSaveTemplate}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonModuleManagement;

